import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product) => {
    let toastShown = false;
    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id);
      if (existingItem) {
        if (!toastShown) {
          toast.success(`Updated ${product.name} quantity in cart`);
          toastShown = true;
        }
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      if (!toastShown) {
        toast.success(`${product.name} added to cart`);
        toastShown = true;
      }
      return [...prevItems, { ...product, quantity: 1 }];
    });
    return true;
  };

  const removeFromCart = (productId, productName) => {
    setCartItems((prevItems) => {
      const itemToRemove = prevItems.find((item) => item.id === productId);
      if (itemToRemove) {
        toast.success(`${productName || itemToRemove.name} removed from cart`);
      }
      return prevItems.filter((item) => item.id !== productId);
    });
  };

  const updateQuantity = (productId, delta) => {
    let toastShown = false;
    setCartItems((prevItems) => {
      const item = prevItems.find((item) => item.id === productId);
      const newQuantity = item ? item.quantity + delta : 1;
      
      if (newQuantity <= 0) {
        return prevItems.filter((item) => item.id !== productId);
      }
      
      if (delta > 0 && item && !toastShown) {
        toast.success(`Added ${item.quantity} of ${item.name} quantity`);
        toastShown = true;
      }
      
      return prevItems
        .map((item) =>
          item.id === productId
            ? { ...item, quantity: Math.max(1, item.quantity + delta) }
            : item
        )
        .filter((item) => item.quantity > 0);
    });
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
